import { useSelector } from "react-redux";
import { Navigate, Outlet } from "react-router-dom";
import Error403Page from "../pages/Error403Page";
import { useEffect, useState, useRef } from "react";
import Loader from "../components/common/Loader";

const PrivateRoute = ({ allowedRoles }) => {
  const { role, token, roles } = useSelector((state) => state.auth);
  const [isReady, setIsReady] = useState(false);
  const lastRoleRef = useRef(role);

  // Give Redux a moment to update after login or role switch
  useEffect(() => {
    setIsReady(false);
    const timer = setTimeout(() => {
      setIsReady(true);
      lastRoleRef.current = role;
    }, 400); // buffer for Redux + axios updates
    return () => clearTimeout(timer);
  }, [role, token]);

  // Determine if we are currently mid-transition or initializing
  const isTransitioning = !isReady || (role !== lastRoleRef.current);

  // Still initializing or switching roles
  if (isTransitioning) {
    return <Loader />;
  }

  // Not logged in → redirect to login (not home) to avoid flicker
  if (!token) {
    console.warn("[PrivateRoute] No token found, redirecting to /login", { path: window.location.pathname });
    return <Navigate to="/login" replace />;
  }

  // Check authorization
  const isAuthorized = allowedRoles.includes(role);

  // Secondary check: If active role doesn't match, check if ANY of the user's 
  // granted roles match. If they do, they are just in the wrong "mode" or 
  // mid-transition. Show loader instead of 403 to avoid flickering.
  const hasCompatibleRole = roles?.some(r => allowedRoles.includes(r));

  if (!isAuthorized) {
    if (hasCompatibleRole) {
      console.log("[PrivateRoute] Role mismatch but user has the required role in their list. Showing loader while transitioning.", {
        currentRole: role,
        allowedRoles,
        rolesList: roles,
        path: window.location.pathname
      });
      return <Loader />;
    }

    console.error("[PrivateRoute] Role unauthorized - genuine 403", {
      currentRole: role,
      allowedRoles,
      rolesInAuth: roles,
      path: window.location.pathname
    });
    return <Error403Page />;
  }

  // KYC Check for Buyers
  // if (role === 'buyer' && user?.kyc_verified === 0) {
  //   return <Navigate to="/market" replace />;
  // }
  // NOTE: Redirection logic for unverified buyers is handled in App.jsx via useEffect 
  // to ensure better control over public/private path transitions.

  // Authorized → render route
  return <Outlet />;
};

export default PrivateRoute;
